<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: Number, required: true },
  inboxId: { type: Number, required: true },
  assignedAgentId: { type: Number, default: 0 },
  assignedTeamId: { type: Number, default: 0 }
});

const emit = defineEmits(['update:show', 'cancel']);

const store = useStore();
const { t } = useI18n();

const assignableAgentsList = useMapGetter('inboxAssignableAgents/getAssignableAgents');
const assignableAgents = computed(() => assignableAgentsList.value(props.inboxId));
const teams = useMapGetter('teams/getTeams');

const selectedAgentId = ref(props.assignedAgentId);
const selectedTeamId = ref(props.assignedTeamId);

import { watch } from 'vue';
watch(() => props.show, (newVal) => {
  if (newVal) {
    selectedAgentId.value = props.assignedAgentId;
    selectedTeamId.value = props.assignedTeamId;
  }
});

const localShow = computed({
  get() { return props.show; },
  set(value) { emit('update:show', value); }
});

const onCancel = () => {
  localShow.value = false;
  emit('cancel');
};

const onSubmit = () => {
  const promises = [];
  
  if (selectedTeamId.value !== 0) {
    promises.push(
      store.dispatch('assignTeam', {
        conversationId: props.conversationId,
        teamId: selectedTeamId.value,
      })
    );
  }

  if (selectedAgentId.value !== 0) {
    promises.push(
      store.dispatch('assignAgent', {
        conversationId: props.conversationId,
        agentId: selectedAgentId.value,
        assigneeType: 'User',
      })
    );
  }
  
  Promise.all(promises).then(() => {
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
    localShow.value = false;
  });
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto relative p-8">
      <h2 class="text-2xl text-n-slate-12 font-medium mb-6">Transfer Ticket</h2>
      <form class="flex flex-col w-full gap-4" @submit.prevent="onSubmit">
        <div class="flex flex-row items-center gap-6 mt-2">
          <label class="text-sm font-medium text-n-slate-12 flex items-center gap-2">
            Transfer to:
            <select v-model="selectedTeamId" class="p-2 pr-8 border border-n-weak rounded text-n-slate-12 bg-n-surface-1 min-w-[150px]">
              <option :value="0">None</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </label>
          <label class="text-sm font-medium text-n-slate-12 flex items-center gap-2">
            Assign to:
            <select v-model="selectedAgentId" class="p-2 pr-8 border border-n-weak rounded text-n-slate-12 bg-n-surface-1 min-w-[150px]">
              <option :value="0">not assigned</option>
              <option v-for="agent in assignableAgents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
          <NextButton faded slate type="reset" label="Cancel" @click.prevent="onCancel" />
          <NextButton type="submit" label="Transfer" color="blue" :disabled="selectedAgentId === props.assignedAgentId && selectedTeamId === props.assignedTeamId" />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
